import { buildSolutionCardCanvas } from "./solutionsCardTexture";

// THREE et GLTFLoader viennent du bundle-three-vendor (chargé avant celui-ci, cf. app/js/ThreeVendor.js) :
// une seule copie de Three.js partagée entre bundle-home/wave/solutions.
const { THREE, GLTFLoader } = window.ThreeVendor;

const CAMERA_FOV = 30;
const CAMERA_DISTANCE = 6.5;
const CARD_SCALE = 0.8;

// Arc marqué : les cartes glissent sur le pourtour d'une roue dont le centre est situé sous le
// bloc — on en voit la portion haute, nettement courbée (effet "sourire"), et chaque carte
// pivote pour rester tangente à la roue (elle "tourne autour de l'arc" au lieu de rester à plat).
// La rotation est une boucle infinie : les cartes réelles sont dupliquées pour peupler tout le
// tour de roue à pas d'angle fixe, afin qu'il y en ait toujours en train d'entrer/sortir sans trou.
// Rayon et pas d'angle règlent ensemble l'écartement des cartes à l'écran (x = sin(angle) × rayon)
// et le creux de l'arc (chute des voisines = rayon × (1 - cos(pas))). Grand rayon + petit pas : les
// cartes restent écartées horizontalement sans que l'arc se creuse trop, contrairement à un petit
// rayon à grand pas qui les faisait plonger.
// 4.4 (au lieu de 4.9) : cartes un peu plus rapprochées, retour recette.
const WIDE_ARC_RADIUS = 4.4;
// Vitesse de croisière du carrousel (mode "auto") : il tourne en permanence, lentement, et le
// scroll (boostSpeed) accélère par-dessus avant de revenir à cette vitesse.
const WIDE_ARC_IDLE_SPEED = 0.15;
// Pas VISÉ seulement : le pas réel est dérivé du nombre de cartes effectivement placées sur le tour
// (cf. _computeWideArcCardCount), qui doit rester un multiple du nombre de solutions.
const WIDE_ARC_ANGLE_STEP_DEG = 20;
// Roulis découplé du pas d'angle : la carte ne se contente plus de rester tangente à la roue, elle
// pivote un peu plus vite que sa progression sur l'arc. Le petit pas nécessaire à un arc peu creusé
// n'écarte donc pas moins les cartes en éventail.
const WIDE_ARC_ROLL_RATIO = 1.4;
// Profondeur strictement croissante de gauche à droite (proportionnelle à l'angle signé, pas à sa
// valeur absolue) : ça lève l'ambiguïté de tri des matériaux transparents entre cartes qui se
// chevauchent (évite le flicker/z-fighting) ET garantit qu'une carte est TOUJOURS devant toute
// carte à sa gauche, quelle que soit sa distance au centre — sinon, côté droit, une carte qui
// s'éloigne du centre reculait plus que sa voisine plus centrale et finissait dessous à tort.
const WIDE_ARC_DEPTH_RATIO = 0.35;
// Inclinaison fixe sur l'axe Y : la carte de gauche passe sous celle de droite à chaque
// chevauchement, comme un jeu de cartes légèrement éventé plutôt qu'une pile bien à plat. L'angle
// reste franc plutôt qu'un simple soupçon de biais : il resserre la largeur projetée de chaque
// carte, donc leur recouvrement mutuel, et il découvre la tranche du modèle.
// L'angle est FIXE, identique pour toutes les cartes et à tout instant : la souris ne le module pas.
// Le faire suivre le pointeur revenait à faire varier la largeur projetée des cartes, donc leur
// recouvrement — pointeur à gauche, elles se présentaient plus de face et se passaient par-dessus.
const WIDE_ARC_TILT_Y_DEG = 28;
// Survol : la carte sous le pointeur se soulève et s'oriente vers lui. Elle conserve sa place dans
// l'empilement (ni avancée vers la caméra, ni bonus de renderOrder) : l'éventail garde son sens de
// recouvrement, chaque carte restant sous sa voisine de droite même soulevée.
const WIDE_ARC_HOVER_LIFT = 0.14;
const WIDE_ARC_HOVER_EASE = 0.12;
// Inclinaison au survol, ajoutée à l'angle fixe des cartes. Contrairement à l'ancien tilt souris,
// elle ne concerne QUE la carte pointée : les autres gardent leur angle, donc leur recouvrement ne
// bouge pas. L'amplitude reste petite pour que la carte, en se présentant plus de face, ne s'élargisse
// pas au point de mordre sur ses voisines.
const WIDE_ARC_HOVER_TILT_YAW = 0.14;
const WIDE_ARC_HOVER_TILT_PITCH = 0.1;
// Fraction de l'écran (en coordonnées normalisées) séparant le centre d'une carte de son bord :
// sert à convertir l'écart pointeur ↔ centre de carte en inclinaison, l'amplitude maximale étant
// atteinte au bord.
const WIDE_ARC_HOVER_TILT_SPAN = 0.25;

const FLAT_CIRCLE_DIAMETER_RATIO = 4;
const FLAT_CIRCLE_ANGLE_STEP_DEG = 22;
// Léger basculement sur l'axe X : au repos les cartes ne sont pas strictement de face (le haut
// s'éloigne un peu de la caméra), ce qui leur donne du volume. L'inclinaison est modulée par la
// position de la carte sur l'arc, donc elle évolue en continu au fil du scroll : une carte se
// redresse en montant vers le centre du cadre puis repart de l'autre côté.
const FLAT_CIRCLE_TILT_X_DEG = 8;
const FLAT_CIRCLE_TILT_X_SCROLL_DEG = 14;
// Même z pour toutes les cartes (transparentes) = ordre de tri du renderer instable pile au
// moment où deux cartes se croisent à l'écran (changement de slide) -> flicker. Un léger
// décalage de profondeur dérivé de l'angle rend cet ordre déterministe, comme WIDE_ARC_DEPTH_RATIO.
const FLAT_CIRCLE_DEPTH_RATIO = 0.05;

function wrapAngle(a) {
	return a - Math.PI * 2 * Math.floor((a + Math.PI) / (Math.PI * 2));
}

export default class SolutionsCardsScene {
	constructor({ canvas, cards, mode, modelUrl, reducedMotion }) {
		this.canvas = canvas;
		this.cards = cards;
		this.mode = mode; // "synced" (bloc principal) | "auto" (bloc carrousel)
		this.modelUrl = modelUrl;
		this.reducedMotion = reducedMotion;

		this.cardHolders = [];
		this.rotation = 0;
		this.targetRotation = 0;
		// Surcroît de vitesse dû au scroll (cf. boostSpeed), ajouté à la vitesse de croisière
		// WIDE_ARC_IDLE_SPEED : il retombe à zéro, le carrousel revient alors à sa rotation lente.
		this.rotationSpeed = 0;
		// Amortissement doux : le carrousel garde du momentum et ralentit progressivement après
		// le scroll au lieu de s'arrêter net, tout en revenant au repos en quelques secondes.
		this.friction = 0.96;
		// Souris consommée par le seul mode "synced", où elle décale la caméra. En mode "auto", les
		// cartes ne réagissent plus au pointeur que par le soulèvement au survol.
		this.mouseX = 0;
		this.mouseY = 0;
		// Dernière position connue du pointeur en coordonnées viewport : le raycast de survol est
		// refait à chaque frame (les cartes défilent sous un pointeur immobile pendant le scroll).
		this.hoverPointer = null;
		this.wideArcCardCount = this._computeWideArcCardCount();

		this._buildScene();
	}

	_buildScene() {
		this.scene = new THREE.Scene();

		const rect = this.canvas.getBoundingClientRect();
		this.camera = new THREE.PerspectiveCamera(CAMERA_FOV, rect.width / Math.max(rect.height, 1), 0.1, 100);
		this.cameraTarget = this.mode === "synced" ? new THREE.Vector3(0, 0, 0) : new THREE.Vector3(0, 0.1, 0);
		this.cameraBaseY = this.mode === "synced" ? 0 : 0.3;
		this.camera.position.set(0, this.cameraBaseY, CAMERA_DISTANCE);
		this.camera.lookAt(this.cameraTarget);

		this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
		this.renderer.setSize(rect.width, Math.max(rect.height, 1), false);
		this.renderer.outputColorSpace = THREE.SRGBColorSpace;

		this.scene.add(new THREE.AmbientLight(0xffffff, 1.7));
		const sun = new THREE.DirectionalLight(0xffffff, 1.3);
		sun.position.set(2, 4, 4);
		this.scene.add(sun);

		this.arcGroup = new THREE.Group();
		this.scene.add(this.arcGroup);

		this._computeFlatCircleGeometry();
	}

	// Nombre de cartes réparties sur le tour de roue en mode "auto". Impérativement un MULTIPLE du
	// nombre de solutions : le tour se refermant sur lui-même, un reste de division y placerait deux
	// exemplaires de la même solution côte à côte à la jonction. On arrondit donc le pas visé au
	// multiple inférieur, ce qui écarte les cartes plutôt que de les resserrer.
	_computeWideArcCardCount() {
		const target = Math.round(360 / WIDE_ARC_ANGLE_STEP_DEG);
		const cycles = Math.max(1, Math.floor(target / this.cards.length));
		return cycles * this.cards.length;
	}

	_computeFlatCircleGeometry() {
		const vFov = THREE.MathUtils.degToRad(this.camera.fov);
		const visibleHeight = 2 * Math.tan(vFov / 2) * CAMERA_DISTANCE;
		this.flatCircleRadius = (visibleHeight * FLAT_CIRCLE_DIAMETER_RATIO) / 2;
		this.flatCircleCenterX = this.flatCircleRadius;
	}

	_makeGLTFLoader() {
		return new GLTFLoader();
	}

	async load() {
		const loader = this._makeGLTFLoader();
		const gltf = await loader.loadAsync(this.modelUrl);
		const template = gltf.scene;
		const flatCircleAngleStep = THREE.MathUtils.degToRad(FLAT_CIRCLE_ANGLE_STEP_DEG);

		// Mode "auto" : on duplique les vraies solutions autant de fois qu'il faut pour peupler
		// tout le tour de roue à pas fixe (cf. _computeWideArcCardCount) — sans ça, avec seulement 3-4
		// solutions réelles espacées sur 360°, la boucle ne montrerait qu'une carte à la fois et
		// laisserait un trou pendant tout le reste du tour.
		const displayCards =
			this.mode === "synced"
				? this.cards
				: Array.from({ length: this.wideArcCardCount }, (_, i) => this.cards[i % this.cards.length]);
		const wideArcAngleStep = (Math.PI * 2) / this.wideArcCardCount;

		await Promise.all(
			displayCards.map(async (card, i) => {
				const cardScene = template.clone(true);
				// Object3D.clone() partage les matériaux par référence : on les clone pour de bon.
				cardScene.traverse((node) => {
					if (node.isMesh && node.material) {
						node.material = node.material.clone();
						// Le modèle carte est composé de plusieurs plans quasi coïncidents (fond, face,
						// icône) : les rendre opaques expose leur ordre de dessin interne au test de
						// profondeur réel et fait apparaître le fond noir par endroits. On les garde
						// transparents (même à pleine opacité) pour préserver leur rendu correct.
						node.material.transparent = true;
					}
				});

				const canvas = await buildSolutionCardCanvas(card);
				const texture = new THREE.CanvasTexture(canvas);
				texture.colorSpace = THREE.SRGBColorSpace;
				texture.flipY = false;
				texture.needsUpdate = true;
				// Upload immédiat : le render de warm-up ci-dessous ne pousse au GPU que les
				// cartes présentes dans le frustum, les autres se seraient uploadées à la
				// volée en entrant dans le champ, pendant la rotation du carrousel.
				this.renderer.initTexture(texture);

				cardScene.traverse((node) => {
					if (node.isMesh && node.material && node.material.name === "logo_zone") {
						node.material.map = texture;
						node.material.side = THREE.FrontSide;
						node.material.needsUpdate = true;
					}
				});

				cardScene.scale.setScalar(CARD_SCALE);

				const holder = new THREE.Group();
				holder.add(cardScene);
				// Lien porté par le holder : il permet de retrouver la solution d'une carte à partir
				// d'un mesh touché par le raycast (cf. linkAtPointer).
				holder.userData.link = card.link;
				holder.userData.baseAngle = this.mode === "synced" ? flatCircleAngleStep * i : wideArcAngleStep * i;
				holder.userData.angle = holder.userData.baseAngle;
				// Progression 0 → 1 du soulèvement au survol, et inclinaison vers le pointeur qui
				// l'accompagne (cf. _updateHoverLift).
				holder.userData.lift = 0;
				holder.userData.tiltYaw = 0;
				holder.userData.tiltPitch = 0;
				this.arcGroup.add(holder);
				this.cardHolders.push(holder);
			})
		);

		this._layout();

		// Compilation des programmes GLSL et upload GPU des textures de cartes avant
		// tout affichage. Sans ça, le premier render() faisait les deux en synchrone
		// dans la frame où le bloc entre à l'écran (~20 ms mesurés, pile pendant le
		// scroll d'arrivée sur le bloc). load() étant appelé au DOMContentLoaded, le
		// coût est absorbé bien avant que le bloc soit atteint. compileAsync s'appuie
		// sur KHR_parallel_shader_compile et laisse le driver travailler en parallèle.
		await this.renderer.compileAsync(this.scene, this.camera);
		this.renderer.render(this.scene, this.camera);
	}

	_layout() {
		if (this.mode === "synced") {
			this._layoutFlatCircle();
		} else {
			this._layoutWideArc();
		}
	}

	_layoutFlatCircle() {
		const tiltXBase = THREE.MathUtils.degToRad(FLAT_CIRCLE_TILT_X_DEG);
		const tiltXScroll = THREE.MathUtils.degToRad(FLAT_CIRCLE_TILT_X_SCROLL_DEG);

		this.cardHolders.forEach((holder) => {
			const raw = holder.userData.baseAngle + this.rotation;
			const angle = Math.PI + raw;

			const x = this.flatCircleCenterX + this.flatCircleRadius * Math.cos(angle);
			const y = this.flatCircleRadius * Math.sin(angle);
			holder.position.set(x, y, -raw * FLAT_CIRCLE_DEPTH_RATIO);
			// sin(raw) plutôt que raw : la modulation reste bornée pour les cartes très éloignées sur
			// l'arc, au lieu de croître indéfiniment avec l'angle.
			holder.rotation.set(-tiltXBase + Math.sin(raw) * tiltXScroll, 0, raw);
			holder.traverse((node) => {
				if (node.isMesh) node.renderOrder = -raw;
			});
		});
	}

	_layoutWideArc() {
		const tiltY = THREE.MathUtils.degToRad(WIDE_ARC_TILT_Y_DEG);
		this.cardHolders.forEach((holder) => {
			// Toutes les cartes suivent la rotation à l'identique, sans décalage/lag entre elles :
			// le bloc de cartes avance d'un seul mouvement rigide.
			holder.userData.angle = holder.userData.baseAngle + this.rotation;
			const delta = wrapAngle(holder.userData.angle);
			// sin(delta), pas delta lui-même : au-delà de ±90°, sin redescend, donc l'angle brut
			// ne reflète plus l'ordre gauche-droite réel à l'écran (cartes proches du bord/fondu).
			// sin(delta) EST la position x utilisée ci-dessous : il reflète toujours le vrai
			// classement gauche-droite, quel que soit l'angle.
			const screenOrder = Math.sin(delta);

			// Centre sous le bloc : seule la portion haute du cercle est visible, nettement
			// courbée. Chaque carte tourne pour rester tangente à la roue à sa position (roulis =
			// l'angle lui-même) : on voit donc les cartes pivoter en suivant la courbe de l'arc.
			const lift = holder.userData.lift;
			holder.position.set(
				screenOrder * WIDE_ARC_RADIUS,
				(Math.cos(delta) - 1) * WIDE_ARC_RADIUS + lift * WIDE_ARC_HOVER_LIFT,
				screenOrder * WIDE_ARC_DEPTH_RATIO
			);
			// Les cartes sont transparentes (cf. load()) : le tri par distance caméra ne suffit pas
			// à garantir un ordre de superposition stable. renderOrder force explicitement l'ordre
			// de dessin de gauche à droite (le renderer ne le lit que sur les meshes réellement
			// dessinés, pas sur le Group qui les contient). Le survol n'y touche pas : une carte
			// soulevée reste sous sa voisine de droite.
			holder.traverse((node) => {
				if (node.isMesh) node.renderOrder = screenOrder;
			});
			// Inclinaison Y fixe, la même pour toutes les cartes et indépendante de la souris — seule
			// la carte survolée y ajoute son inclinaison vers le pointeur.
			holder.rotation.set(holder.userData.tiltPitch, tiltY + holder.userData.tiltYaw, -delta * WIDE_ARC_ROLL_RATIO);
			// La disparition aux extrémités est gérée par le gradient CSS du conteneur (cf.
			// block-solutions-carousel.scss) : pas de fondu d'opacité par carte. On coupe juste net
			// au-delà de ±90°, là où sin(delta) rebroussant chemin ne reflète plus l'ordre
			// gauche-droite réel — cette carte est de toute façon déjà hors champ à cet angle.
			holder.visible = Math.abs(delta) < Math.PI / 2;
		});
	}

	setActiveIndex(index, animate = true) {
		const angleStep =
			this.mode === "synced"
				? THREE.MathUtils.degToRad(FLAT_CIRCLE_ANGLE_STEP_DEG)
				: (Math.PI * 2) / this.wideArcCardCount;
		this.targetRotation = -angleStep * index;
		if (!animate || this.reducedMotion) {
			this.rotation = this.targetRotation;
		}
	}

	setContinuousIndex(value) {
		if (this.mode !== "synced") return;
		const angleStep = THREE.MathUtils.degToRad(FLAT_CIRCLE_ANGLE_STEP_DEG);
		this.targetRotation = -angleStep * value;
		this.rotation = this.targetRotation;
	}

	boostSpeed(amount) {
		if (this.mode !== "auto" || this.reducedMotion) return;
		this.rotationSpeed = Math.min(9, this.rotationSpeed + amount);
	}

	setMouseTilt(x, y) {
		if (this.reducedMotion) return;
		this.mouseX = x;
		this.mouseY = y;
	}

	// Position du pointeur en coordonnées viewport, consommée par _updateHoverLift à chaque frame.
	setHoverPointer(clientX, clientY) {
		if (this.mode !== "auto" || this.reducedMotion) return;
		if (!this.hoverPointer) this.hoverPointer = { x: 0, y: 0 };
		this.hoverPointer.x = clientX;
		this.hoverPointer.y = clientY;
	}

	// Carte située sous le pointeur, null si le pointeur est hors du canvas ou ne touche aucune
	// carte. Plusieurs cartes sont visibles à la fois : c'est la géométrie réelle qui décide, pas la
	// carte centrale.
	_holderAtPointer(clientX, clientY) {
		const rect = this.canvas.getBoundingClientRect();
		if (!rect.width || !rect.height) return null;
		// Le raycast reste géométriquement valide hors du frustum : sans cette garde, un pointeur
		// posé ailleurs dans la page pourrait "toucher" une carte (le survol suit une souris
		// globale, cf. SolutionsCards3D).
		if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) return null;

		if (!this.raycaster) {
			this.raycaster = new THREE.Raycaster();
			this.pointer = new THREE.Vector2();
		}

		this.pointer.set(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
		this.raycaster.setFromCamera(this.pointer, this.camera);

		// Le raycaster de Three ne tient pas compte de `visible` : on écarte nous-mêmes les cartes
		// masquées (cf. holder.visible dans _layoutWideArc), sinon on pourrait "toucher" une carte
		// située hors champ.
		const hit = this.raycaster.intersectObjects(
			this.cardHolders.filter((holder) => holder.visible),
			true
		)[0];
		if (!hit) return null;

		// Le mesh touché est un plan du modèle : on remonte jusqu'au holder, qui porte le lien.
		let node = hit.object;
		while (node && node.userData.link === undefined) node = node.parent;
		return node || null;
	}

	// Lien de la solution portée par la carte située sous le pointeur, null s'il n'y en a aucune.
	linkAtPointer(clientX, clientY) {
		const holder = this._holderAtPointer(clientX, clientY);
		return holder ? holder.userData.link : null;
	}

	// Soulèvement et inclinaison de la carte survolée. Le raycast est refait à chaque frame plutôt
	// qu'au seul mousemove : les cartes défilent au scroll, donc celle qui se trouve sous un pointeur
	// immobile change sans qu'aucun événement souris ne soit émis.
	_updateHoverLift() {
		const hovered = this.hoverPointer ? this._holderAtPointer(this.hoverPointer.x, this.hoverPointer.y) : null;

		// Écart entre le pointeur et le centre de la carte, d'où elle tire son inclinaison : elle se
		// tourne vers lui. _holderAtPointer vient de poser this.pointer aux coordonnées normalisées du
		// pointeur, il ne reste qu'à y projeter le centre de la carte.
		let towardX = 0;
		let towardY = 0;
		if (hovered) {
			if (!this._hoveredCenter) this._hoveredCenter = new THREE.Vector3();
			this._hoveredCenter.copy(hovered.position).project(this.camera);
			towardX = THREE.MathUtils.clamp((this.pointer.x - this._hoveredCenter.x) / WIDE_ARC_HOVER_TILT_SPAN, -1, 1);
			towardY = THREE.MathUtils.clamp((this.pointer.y - this._hoveredCenter.y) / WIDE_ARC_HOVER_TILT_SPAN, -1, 1);
		}

		this.cardHolders.forEach((holder) => {
			const isHovered = holder === hovered;
			const lift = isHovered ? 1 : 0;
			const yaw = isHovered ? towardX * WIDE_ARC_HOVER_TILT_YAW : 0;
			const pitch = isHovered ? towardY * WIDE_ARC_HOVER_TILT_PITCH : 0;

			holder.userData.lift += (lift - holder.userData.lift) * WIDE_ARC_HOVER_EASE;
			holder.userData.tiltYaw += (yaw - holder.userData.tiltYaw) * WIDE_ARC_HOVER_EASE;
			holder.userData.tiltPitch += (pitch - holder.userData.tiltPitch) * WIDE_ARC_HOVER_EASE;
		});
	}

	update() {
		if (this.mode === "auto") {
			this.rotationSpeed *= this.friction;
			const idleSpeed = this.reducedMotion ? 0 : WIDE_ARC_IDLE_SPEED;
			this.rotation += (idleSpeed + this.rotationSpeed) * 0.015;
			this._updateHoverLift();
		} else {
			this.rotation += (this.targetRotation - this.rotation) * 0.08;
		}

		this._layout();

		if (this.mode === "synced") {
			const tiltX = this.mouseX * 3.3;
			const tiltY = this.mouseY * 1.65;
			this.camera.position.x += (tiltX - this.camera.position.x) * 0.06;
			this.camera.position.y += (this.cameraBaseY - tiltY - this.camera.position.y) * 0.06;
			this.camera.lookAt(this.cameraTarget);
		}

		this.renderer.render(this.scene, this.camera);
	}

	resize() {
		const rect = this.canvas.getBoundingClientRect();
		this.camera.aspect = rect.width / Math.max(rect.height, 1);
		this.camera.updateProjectionMatrix();
		this.renderer.setSize(rect.width, Math.max(rect.height, 1), false);
		this._computeFlatCircleGeometry();
	}

	destroy() {
		this.renderer.dispose();
		this.cardHolders.forEach((holder) => {
			holder.traverse((node) => {
				if (node.isMesh) {
					node.geometry.dispose();
					if (node.material.map) node.material.map.dispose();
					node.material.dispose();
				}
			});
		});
	}
}
